import { Tab, Menu, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "../../../../helpers/functions";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../common/New-ToolTip";
import MedicationNameToolTip from "../../../common/tooltip/medication-name";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMedications,
  updateMedication,
} from "../../../../redux/modules/clinical-services/actions";
import { setSelectedMedicationId } from "../../../../redux/modules/medications/actions";
import MedicationCreate from "../service-steps/clinical-assessment/MedicationCreate";
import DiscontinueDialog from "../medications/DiscontinueDialog";

const Vaccines = ({open = false}) => {
  const { medications = [] } = useSelector((state) => state.clinicalService || {});
  const dispatch = useDispatch();
  const [showVaccine, setShowVaccine] = useState(open);
  const [filteredVaccines, setFilteredVaccines] = useState([]);
  const [selectedMedsForAccordion, setSelectedMedsForAccordion] = useState([]);
  const [focusItem, setFocusItem] = useState(null);
  const [hoveredMed, setHoveredMed] = useState(null);
  const { id: patientId } = useParams();
  const [selectedFilter, setSelectedFilter] = useState("active");
  const [selectedSort, setSelectedSort] = useState("alphabetic");
  const [clickedMedication, setClickedMedication] = useState(null);
  const [showMedicationSlider, setShowMedicationSlider] = useState(false);
  const [expandedNames, setExpandedNames] = useState([]);
  const [showVaccineList, setShowVaccineList] = useState(true);
  const [openDiscontinuePopup, setOpenDiscontinuePopup] = useState(false);
  const [discontinueDrug, setDiscontinueDrug] = useState(null);

  const sourceVaccines = useMemo(
    () =>
      (medications || [])
        .filter((med) => String(med?.medicationType || "").toLowerCase() === "vaccine")
        .map((med) => ({
          ...med,
          vaccineName: med?.vaccineName || med?.medicationName || "",
        })),
    [medications]
  );

  //state management
  useEffect(() => {
    dispatch(fetchMedications(patientId));
  }, [dispatch, patientId]);

  useEffect(() => {
    filterMedications();
  }, [selectedFilter, selectedSort, sourceVaccines]);

  const sortMedications = (meds) => {
    if (selectedSort == "alphabetic") {
      setFilteredVaccines(
        [...meds].sort((a, b) => a.vaccineName.localeCompare(b.vaccineName))
      );
    } else if (selectedSort == "newest") {
      setFilteredVaccines(
        [...meds].sort((a, b) => new Date(b.created) - new Date(a.created))
      );
    } else if (selectedSort == "oldest") {
      setFilteredVaccines(
        [...meds].sort((a, b) => new Date(a.created) - new Date(b.created))
      );
    }
  };

  const filterMedications = () => {
    let meds = sourceVaccines;
    if (selectedFilter === "minorAilment") {
      meds = sourceVaccines.filter((x) => x.isRx);
    } else {
      meds = sourceVaccines.filter(
        (x) => selectedFilter == "all" || x.status === selectedFilter
      );
    }
    setFilteredVaccines(meds);
    sortMedications(meds);
  };

  const expandMedicine = (id) => {
    if (selectedMedsForAccordion.includes(id)) {
      setSelectedMedsForAccordion(
        selectedMedsForAccordion.filter((item) => item !== id)
      );
    } else {
      setSelectedMedsForAccordion([...selectedMedsForAccordion, id]);
    }
  };

  const updateMedicationStatus = (medication, status, discontinueData = null) => {
    const updatedMedicationObject = {
      ...medication,
      status,
      ...(discontinueData && {
        inactiveDate: discontinueData.inactiveDate,
        inactiveWhen: discontinueData.whenDiscontinued,
        inactiveInitiator: discontinueData.source,
        inactiveCommunicationType: discontinueData.communicationType,
        inactiveReason: discontinueData.reason,
        inactiveOtherReason: discontinueData.otherReason,
      }),
    };
    dispatch(updateMedication(patientId, { medications: [updatedMedicationObject] }));
  };

  return (
    <>
      {showVaccine && (
        <div className="grid w-[318px]">
          <div className="flex flex-col flex-1 pt-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h5 className="text-xl font-bold text-teal-300">Vaccines</h5>
                <Menu
                  as="div"
                  className="relative inline-block text-left pt-[6px]"
                >
                <div className="flex items-center">
                  <Menu.Button>
                    <ReactSVG src="/icons/filter_list.svg" className="pl-2 " />{" "}
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                  enterTo="transform translate-y-0 opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                >
                  <Menu.Items className="min-w-[200px] absolute z-10 left-0 w-40 origin-top-left bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-opacity-5">
                    <Menu.Item>
                      <button
                        className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                          ${
                            selectedFilter === "all" && "bg-blue-100"
                          } hover:bg-gray-50`}
                        onClick={(e) => {
                          setSelectedFilter("all");
                        }}
                      >
                        All
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                          ${
                            selectedFilter === "active" && "bg-blue-100"
                          } hover:bg-gray-50`}
                        onClick={(e) => {
                          setSelectedFilter("active");
                        }}
                      >
                        Active
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                          ${
                            selectedFilter === "inactive" && "bg-blue-100"
                          } hover:bg-gray-50`}
                        onClick={(e) => {
                          setSelectedFilter("inactive");
                        }}
                      >
                        Inactive
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                          ${
                            selectedFilter === "minorAilment" && "bg-blue-100"
                          } hover:bg-gray-50`}
                        onClick={(e) => {
                          // setSelectedFilter("minorAilment");
                        }}
                      >
                        Minor Ailments
                      </button>
                    </Menu.Item>
                    <Menu.Item className="relative">
                      <Menu
                        as="div"
                        className="block w-full px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50"
                      >
                        <div>
                          <Menu.Button className="w-full flex flex-row items-center justify-between text-left">
                            <span>Sort Order</span>
                            <ChevronRightIcon className="w-4 h-4" />
                          </Menu.Button>
                        </div>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform translate-y-2 opacity-0 scale-95"
                          enterTo="transform translate-y-0 opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform translate-y-0 opacity-100 scale-100"
                          leaveTo="transform translate-y-2 opacity-0 scale-95"
                        >
                          <Menu.Items className="min-w-fit absolute top-0 left-16 translate-x-full bg-white divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <Menu.Item>
                              <span
                                className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                                  ${
                                    selectedSort === "alphabetic" &&
                                    "bg-blue-100"
                                  } hover:bg-gray-50`}
                                onClick={(e) => {
                                  setSelectedSort("alphabetic");
                                }}
                              >
                                Alphabetical
                              </span>
                            </Menu.Item>
                            <Menu.Item>
                              <button
                                className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                                  ${
                                    selectedSort === "newest" && "bg-blue-100"
                                  } hover:bg-gray-50`}
                                onClick={(e) => {
                                  setSelectedSort("newest");
                                }}
                              >
                                Newest on top
                              </button>
                            </Menu.Item>
                            <Menu.Item>
                              <button
                                className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                                  ${
                                    selectedSort === "oldest" && "bg-blue-100"
                                  } hover:bg-gray-50`}
                                onClick={(e) => {
                                  setSelectedSort("oldest");
                                }}
                              >
                                Oldest on top
                              </button>
                            </Menu.Item>
                            <Menu.Item>
                              <button
                                className="opacity-40 block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50"
                                disabled={true}
                              >
                                Custom range
                              </button>
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
              </div>
              <button
                className="text-REELTeal text-xl font-bold px-2"
                onClick={() => {
                  setClickedMedication(null);
                  setShowMedicationSlider(true);
                }}
              >
                +ADD
              </button>
            </div>
            <div>
              <Tab.Group>
                <Tab.Panels className="w-[318px]">
                  <Tab.Panel
                    className={classNames(
                      "rounded-xl bg-white py-3",
                      "focus:outline-none"
                    )}
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center rounded-[4px] border border-gray-100 px-3 py-2">
                        <ReactSVG src="/icons/interactions.svg" className="pr-2" />
                        <p className="text-sm font-semibold text-gray-800">
                          Interactions
                        </p>
                      </div>
                      <div
                        className={`cursor-pointer rounded-[4px] flex justify-between h-[40px] pl-[12px] pr-[10px] border border-gray-100 ${
                          showVaccineList ? "bg-gray-50" : "bg-gray-10"
                        } items-center`}
                        onClick={() => setShowVaccineList((prev) => !prev)}
                      >
                        <p
                          className={`text-gray-800 text-sm ${
                            showVaccineList ? "font-semibold" : "font-medium"
                          }`}
                        >
                          Vaccines
                        </p>
                        <ChevronDownIcon
                          className={`w-4 h-4 transition-transform ${
                            showVaccineList ? "-rotate-180" : ""
                          }`}
                        />
                      </div>
                    </div>
                    {showVaccineList && (
                      <ul
                        className="divide-y divide-gray-100 border-y border-y-gray-100"
                        onMouseLeave={() => {
                          setHoveredMed(null);
                        }}
                      >
                        {filteredVaccines?.length > 0 &&
                          filteredVaccines?.map((med, i) => (
                          <li
                            key={med.id}
                            className={`text-sm text-teal-600`}
                            onClick={() => {
                              const nextSelectedId = focusItem === med.id ? null : med.id;
                              setFocusItem(nextSelectedId);
                              dispatch(setSelectedMedicationId(nextSelectedId));
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              setExpandedNames((prev) =>
                                prev.includes(med.id)
                                  ? prev.filter((id) => id !== med.id)
                                  : [...prev, med.id]
                              );
                              if (!selectedMedsForAccordion.includes(med.id)) {
                                setSelectedMedsForAccordion((prev) => [...prev, med.id]);
                              }
                            }}
                            onMouseEnter={() => {
                              setHoveredMed(med.id);
                            }}
                          >
                            <div
                              className={`${
                                selectedMedsForAccordion.includes(med.id) ||
                                focusItem === med.id
                                  ? "bg-blue-100"
                                  : ""
                              } hover:bg-gray-50`}
                            >
                              <div
                                className={`pl-[28px] pr-3 flex items-center justify-between h-[36px]`}
                              >
                                <button
                                  type="button"
                                  className="text-left focus:outline-none"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const nextSelectedId = focusItem === med.id ? null : med.id;
                                    setFocusItem(nextSelectedId);
                                    dispatch(setSelectedMedicationId(nextSelectedId));
                                  }}
                                >
                                  <span
                                    data-tooltip-place="bottom"
                                    data-tooltip-id={med.id}
                                    className={`${
                                      med.status === "inactive" && "opacity-40"
                                    }`}
                                  >
                                    {(med.vaccineName || "").length > 25 &&
                                    !expandedNames.includes(med.id)
                                      ? med.vaccineName.slice(0, 25) + "..."
                                      : med.vaccineName}{" "}
                                  </span>
                                </button>
                                <div className="flex items-center space-x-3 ">
                                  {hoveredMed === med.id && (
                                    <>
                                      {med.isRx && (
                                        <button className="">
                                          <ReactSVG src="/icons/rx-medicine.svg" />
                                        </button>
                                      )}
                                      <Menu as="div" className="relative ">
                                        <div>
                                          <Menu.Button
                                            onClick={() => {
                                              setFocusItem(med.id);
                                            }}
                                          >
                                            <ReactSVG
                                              src={`/icons/triple-dot.svg`}
                                              className={`flex !items-center `}
                                            />
                                          </Menu.Button>
                                        </div>
                                        <Transition
                                          as={Fragment}
                                          enter="transition ease-out duration-100"
                                          enterFrom="transform translate-y-2 opacity-0 scale-95"
                                          enterTo="transform translate-y-0 opacity-100 scale-100"
                                          leave="transition ease-in duration-75"
                                          leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                          leaveTo="transform translate-y-2 opacity-0 scale-95"
                                        >
                                          <Menu.Items className="absolute z-[200] top-0 left-4 w-36 origin-top-left bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                                            <Menu.Item>
                                              <button
                                                className=" block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50"
                                                onClick={() => {
                                                  setDiscontinueDrug(med);
                                                  setOpenDiscontinuePopup(true);
                                                }}
                                              >
                                                Discontinue
                                              </button>
                                            </Menu.Item>
                                            <Menu.Item>
                                              <button className=" block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50">
                                                History
                                              </button>
                                            </Menu.Item>
                                            <Menu.Item>
                                              <button className=" block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50">
                                                Hold
                                              </button>
                                            </Menu.Item>
                                            <Menu.Item>
                                              <button className=" block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50">
                                                Modify
                                              </button>
                                            </Menu.Item>
                                            <Menu.Item>
                                              <button
                                                className=" block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50"
                                                onClick={() => {}}
                                              >
                                                Resume
                                              </button>
                                            </Menu.Item>
                                            <Menu.Item>
                                              <button className=" block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50">
                                                Void
                                              </button>
                                            </Menu.Item>
                                          </Menu.Items>
                                        </Transition>
                                      </Menu>
                                    </>
                                  )}
                                  <button
                                    className=""
                                    onClick={() => {
                                      expandMedicine(med.id);
                                    }}
                                  >
                                    {!selectedMedsForAccordion.includes(
                                      med.id
                                    ) && (
                                      <ChevronDownIcon
                                        className="w-4 h-4"
                                        strokeWidth={2}
                                      />
                                    )}
                                    {selectedMedsForAccordion.includes(
                                      med.id
                                    ) && (
                                      <ChevronUpIcon
                                        className="w-4 h-4"
                                        strokeWidth={2}
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                            {selectedMedsForAccordion.includes(med.id) && (
                              <div className="p-3">
                                <div className="px-4 gap-2 font-normal text-xs flex flex-col">
                                  <div
                                    className={`flex flex-row justify-between items-center border-b-[1px] border-b-gray-100`}
                                  >
                                    <span>DIN: 01234567</span>
                                    <span className="font-semibold text-REELTeal">
                                      Unfilled
                                    </span>
                                  </div>
                                  <div className="flex flex-row items-center justify-between">
                                    <span>First Dose: 10-Apr-2024</span>
                                    <span>QTY: 2 </span>
                                  </div>
                                  <div className="flex flex-row items-center justify-between">
                                    <span>
                                      Booster:{" "}
                                      <span className="pl-3">10-Nov-2024</span>
                                    </span>
                                    <span>Refill: 1</span>
                                  </div>
                                </div>
                              </div>
                            )}
                            {med.vaccineName.length > 25 && (
                              <NewToolTip
                                id={med.id}
                                render={() => (
                                  <MedicationNameToolTip
                                    name={med.vaccineName}
                                  />
                                )}
                              />
                            )}
                          </li>
                          ))}
                      </ul>
                    )}
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </div>
        </div>
      )}
      <div className="mx-2 flex justify-center items-center w-[7px] h-auto bg-gray-100">
        {/* <div className=""></div> */}
        {!showVaccine && (
          <button
            className="rounded-full w-5 h-5 bg-teal-600 mb-[100px]  border-teal-600 text-white inline-flex duration-300 items-center justify-center px-1 py-1"
            onClick={() => {
              setShowVaccine(true);
            }}
          >
            <ChevronRightIcon className="w-4 h-4 text-white" />
          </button>
        )}
        {showVaccine && (
          <button
            className="rounded-full w-5 h-5 bg-teal-600 mb-[100px]  border-teal-600 text-white inline-flex duration-300 items-center justify-center px-1 py-1"
            onClick={() => {
              setShowVaccine(false);
            }}
          >
            <ChevronLeftIcon className="w-4 h-4 text-white" />
          </button>
        )}
      </div>
      <MedicationCreate
        create={clickedMedication?.id ? false : true}
        patientId={patientId}
        type="Medication"
        selectedDrugType="Vaccine"
        open={showMedicationSlider}
        fullMedication={clickedMedication}
        step={1}
        maxSteps={1}
        title="Vaccine"
        onClose={() => {
          setClickedMedication(null);
          setShowMedicationSlider(false);
        }}
        onSave={() => {
          setShowMedicationSlider(false);
          dispatch(fetchMedications(patientId));
        }}
      />
      <DiscontinueDialog
        open={openDiscontinuePopup}
        drug={discontinueDrug}
        onCross={() => {
          setOpenDiscontinuePopup(false);
          setDiscontinueDrug(null);
        }}
        handleClose={() => {
          setOpenDiscontinuePopup(false);
          setDiscontinueDrug(null);
        }}
        confirmAction={(discontinueData) => {
          updateMedicationStatus(discontinueDrug, "inactive", discontinueData);
          setOpenDiscontinuePopup(false);
          setDiscontinueDrug(null);
        }}
      />
    </>
  );
};

export default Vaccines;
